<template>

  <div
    class="border overflow-auto  border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0">

    <div class="h-auto  flex flex-col w-[600px]">

      <woot-modal-header :header-title="$t('CAMPAIGN_MANAGER.ADD.TITLE')"
        :header-content="$t('CAMPAIGN_MANAGER.ADD.DESC')" />
      <div class="px-8 pt-8">
        <form class="flex flex-col w-full" @submit.prevent="addCampaign">
          <div class="w-full">

            <label>Delivery Time</label>
            <div :class="{ error: $v.deliveryType.$error }">
              <input type="hidden" v-model="deliveryType" />
              <div class="radio-group">
                <label class="radio-container">
                  <input type="radio" name="name" value="sendNow" v-model="scheduleOption" />
                  <span class="checkmark"></span>
                  Send Now
                </label>
                <label class="radio-container">
                  <input type="radio" name="name" value="sendLater" v-model="scheduleOption" />
                  <span class="checkmark"></span>
                  Schedule for later
                </label>
              </div>
              <span v-if="$v.deliveryType.$error" class="message">
                Delivery Time required
              </span>
            </div>

            <div class="grid grid-cols-2 gap-4 mb-4 ">

              <div>
                <label>
                  Start Schedule time
                  <woot-date-time-picker :value="startScheduleTime" :disabled="deliveryType == 'sendNow'"
                    :confirm-text="$t('CAMPAIGN_MANAGER.ADD.FORM.SCHEDULED_AT.CONFIRM')"
                    :placeholder="$t('CAMPAIGN_MANAGER.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')" @change="onChangeStart" />
                </label>
                
                <span v-if="$v.startScheduleTime.$error || manualValidationErrors.startScheduleTime" class="message">
                  {{ manualValidationErrors.startScheduleTime || 'Start Schedule time must be at least 5 minutes from now' }}
                </span>
              </div>

              <div>
                <label>
                  End Schedule time
                  <woot-date-time-picker :value="endScheduleTime"
                    :confirm-text="$t('CAMPAIGN_MANAGER.ADD.FORM.SCHEDULED_AT.CONFIRM')"
                    :placeholder="$t('CAMPAIGN_MANAGER.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')" @change="onChangeEnd" />
                </label>
                <span v-if="$v.endScheduleTime.$error || manualValidationErrors.endScheduleTime" class="message">
                  {{ manualValidationErrors.endScheduleTime || (deliveryType == 'sendNow' ? 'End Schedule time must be at least 30 minutes from now' : 'End Schedule time must be at least 30 minutes after Start Schedule time') }}
                </span>
              </div>
            </div>


          </div>

          <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
            <woot-button type="button" variant="clear" @click.prevent="previous">
              << Previous </woot-button>
              
              <woot-button type="button"  @click="openSendTestPopup()">
                  Send Test Message
                </woot-button>
              
                <woot-button :is-disabled="$v.$invalid || uiFlags.isCreating" :is-loading="uiFlags.isCreating">
                  {{ submitBtnText }}
                </woot-button>
          </div>
        </form>
      </div>
    </div>

    <woot-modal :show.sync="showSendTestPopup" :on-close="hideSendTestPopup">
      <SendTestMessage :on-close="hideSendTestPopup"></SendTestMessage>
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';

import alertMixin from 'shared/mixins/alertMixin';
import campaignMixin from 'shared/mixins/campaignMixin';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import SendTestMessage from './SendTestMessage.vue';
import { buildCampaignOutboundParams } from 'dashboard/helper/buildCampaignOutboundParams';

export default {
  components: {
    WootDateTimePicker,
    SendTestMessage
  },

  mixins: [alertMixin, campaignMixin],
  data() {
    return {
      scheduleOption: '',
      submitBtnText: 'Schedule',
      showSendTestPopup: false,
      manualValidationErrors: {
        startScheduleTime: null,
        endScheduleTime: null
      }
    };
  },

  watch: {
    scheduleOption(newValue) {

      if (newValue == 'sendNow') {
        this.submitBtnText = "Send Now"
      }

      if (newValue == 'sendLater') {
        this.submitBtnText = "Schedule"
      }

      this.$store.dispatch('campaignsManager/updateSendNow', newValue === 'sendNow');
      this.$store.dispatch('campaignsManager/updateSendLater', newValue === 'sendLater');
      
      // Revalidar endScheduleTime ya que la validación depende de deliveryType
      if (this.endScheduleTime) {
        this.$v.endScheduleTime.$touch();
      }
    }
  },

  validations() {
    const commonValidations = {
      deliveryType: { required },
      startScheduleTime: this.deliveryType == "sendNow" ? {} : { 
        required,
        minMinutesFromNow(value) {
          if (!value) return true; // required ya maneja el caso de valor vacío
          const now = new Date();
          const minDate = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutos desde ahora
          return value instanceof Date && value > minDate;
        }
      },
      endScheduleTime: {
        required,

        isAfterStartDate(value) {
          if (!value) return true; // required ya maneja el caso de valor vacío
          
          // Si deliveryType es "sendNow", validar contra la fecha/hora actual del sistema
          if (this.deliveryType == "sendNow") {
            const now = new Date();
            const minEndDate = new Date(now.getTime() + 30 * 60 * 1000); // 30 minutos desde ahora
            return value instanceof Date && value >= minEndDate;
          }
          
          // Si deliveryType es "sendLater", validar contra startScheduleTime
          if (!this.startScheduleTime) return true; // Si no hay startScheduleTime, no validar esta regla
          const minEndDate = new Date(this.startScheduleTime.getTime() + 30 * 60 * 1000); // 30 minutos después de startScheduleTime
          return value instanceof Date && value >= minEndDate;
        }
      }
    };

    return {
      ...commonValidations
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaignsManager/getUIFlags',
      campaign: 'campaignsManager/getCampaign',
    }),

    sendNow: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        return campaing.sendNow
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateSendNow', value);
      }
    },
    sendLater: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        return campaing.sendLater
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateSendLater', value);
      }
    },
    startScheduleTime: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        const dateValue = campaing.startScheduleTime;
        // Si viene como string ISO desde el backend, convertir a Date para mostrar en hora local
        if (dateValue && typeof dateValue === 'string') {
          return new Date(dateValue);
        }
        // Si ya es un objeto Date, JavaScript lo mostrará automáticamente en hora local del navegador
        return dateValue;
      },
      set(value) {
        this.$store.dispatch('campaignsManager/UpdateStartScheduleTime', value);
      }
    },
    deliveryType: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        return campaing.deliveryType
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateDeliveryType', value);
      }
    },
    endScheduleTime: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        const dateValue = campaing.endScheduleTime;
        // Si viene como string ISO desde el backend, convertir a Date para mostrar en hora local
        if (dateValue && typeof dateValue === 'string') {
          return new Date(dateValue);
        }
        // Si ya es un objeto Date, JavaScript lo mostrará automáticamente en hora local del navegador
        return dateValue;
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateEndScheduleTime', value);
      }
    },
    showStartScheduleTimeError() {
      // Solo mostrar error si deliveryType no es "sendNow" y la validación existe y falla
      if (this.deliveryType === 'sendNow') return false;
      if (!this.$v.startScheduleTime) return false;
      // Verificar si el campo ha sido tocado y tiene errores
      if (!this.$v.startScheduleTime.$dirty && !this.$v.startScheduleTime.$error) return false;
      if (!this.$v.startScheduleTime.minMinutesFromNow) return false;
      return !this.$v.startScheduleTime.minMinutesFromNow;
    },
    showEndScheduleTimeError() {
      // Verificar de forma segura si debe mostrarse el error de endScheduleTime
      if (!this.$v.endScheduleTime) return false;
      // Verificar si el campo ha sido tocado y tiene errores
      if (!this.$v.endScheduleTime.$dirty && !this.$v.endScheduleTime.$error) return false;
      if (!this.$v.endScheduleTime.isAfterStartDate) return false;
      return !this.$v.endScheduleTime.isAfterStartDate;
    }

  },
  mounted() {

  },
  methods: {
    previous() {
      this.$router.push({
        name: 'campaigns_template'
      });
    },
    onClose() {
      this.$emit('on-close');
    },
    onChange(value) {
      this.scheduledAt = value;
    },
    onChangeStart(value) {
      console.log(value)
      // Limpiar error manual cuando el usuario cambia el valor
      this.manualValidationErrors.startScheduleTime = null;
      this.$store.dispatch('campaignsManager/updateStartScheduleTime', value);
      // Esperar a que Vue actualice el estado antes de validar
      this.$nextTick(() => {
        // Activar validación cuando cambia el valor
        this.$v.startScheduleTime.$touch();
        // Revalidar endScheduleTime ya que depende de startScheduleTime
        if (this.endScheduleTime) {
          this.$v.endScheduleTime.$touch();
        }
      });
    },
    onChangeEnd(value) {
      // Limpiar error manual cuando el usuario cambia el valor
      this.manualValidationErrors.endScheduleTime = null;
      this.$store.dispatch('campaignsManager/updateEndScheduleTime', value);
      // Esperar a que Vue actualice el estado antes de validar
      this.$nextTick(() => {
        // Activar validación cuando cambia el valor
        this.$v.endScheduleTime.$touch();
      });
    },

    getCampaignDetails() {
      let campaignDetails = null;
      if (this.isOngoingType) {
        campaignDetails = {
          title: this.title,
          message: this.message,
          inbox_id: this.selectedInbox,
          sender_id: this.selectedSender || null,
          enabled: this.enabled,
          trigger_only_during_business_hours:
            // eslint-disable-next-line prettier/prettier
            this.triggerOnlyDuringBusinessHours,
          trigger_rules: {
            url: this.endPoint,
            time_on_page: this.timeOnPage,
          },
        };
      } else {
        const audience = this.selectedAudience.map(item => {
          return {
            id: item.id,
            type: 'Label',
          };
        });
        campaignDetails = {
          title: this.title,
          message: this.message,
          inbox_id: this.selectedInbox,
          scheduled_at: this.scheduledAt,
          audience,
        };
      }
      return campaignDetails;
    },
    async addCampaign() {
      // Limpiar errores de validación manual previos
      this.manualValidationErrors.startScheduleTime = null;
      this.manualValidationErrors.endScheduleTime = null;
      
      // Ejecutar validaciones de vuelidate
      this.$v.$touch();
      
      // Validar manualmente con la fecha/hora actual más reciente
      // Esto es importante porque aunque las fechas fueron válidas al seleccionarlas,
      // el usuario puede demorarse en llenar el formulario y los tiempos pueden haberse vencido
      const now = new Date();
      let hasManualErrors = false;
      
      // Validar startScheduleTime
      if (this.deliveryType !== 'sendNow' && this.startScheduleTime) {
        const minDate = new Date(now.getTime() + 1 * 60 * 1000); // 5 minutos desde ahora
        if (!(this.startScheduleTime instanceof Date) || this.startScheduleTime <= minDate) {
          this.manualValidationErrors.startScheduleTime = 'Start Schedule time must be at least 5 minutes from now';
          hasManualErrors = true;
        }
      }
      
      // Validar endScheduleTime
      if (this.endScheduleTime) {
        let isValid = true;
        let errorMessage = '';
        
        if (this.deliveryType === 'sendNow') {
          const minEndDate = new Date(now.getTime() + 30 * 60 * 1000); // 30 minutos desde ahora
          isValid = this.endScheduleTime instanceof Date && this.endScheduleTime >= minEndDate;
          errorMessage = 'End Schedule time must be at least 30 minutes from now';
        } else if (this.startScheduleTime) {
          const minEndDate = new Date(this.startScheduleTime.getTime() + 30 * 60 * 1000); // 30 minutos después de startScheduleTime
          isValid = this.endScheduleTime instanceof Date && this.endScheduleTime >= minEndDate;
          errorMessage = 'End Schedule time must be at least 30 minutes after Start Schedule time';
        }
        
        if (!isValid) {
          this.manualValidationErrors.endScheduleTime = errorMessage;
          hasManualErrors = true;
        }
      }
      
      // Si hay errores de validación manual o de vuelidate, detener el submit
      if (hasManualErrors || this.$v.$invalid) {
        return;
      }

      // try {
      const campaignDetails = this.getPayLoad();
      await this.$store.dispatch('campaignsManager/create', campaignDetails);
      this.$store.dispatch('campaignsManager/clearState');
      this.showAlert(this.$t('CAMPAIGN_MANAGER.ADD.API.SUCCESS_MESSAGE'));
      
      this.$router.push({
        name: 'list_campaigns_all'
      });

      //this.onClose();
      /*
            } catch (error) {
              const errorMessage =
                error?.response?.message || this.$t('CAMPAIGN_MANAGER.ADD.API.ERROR_MESSAGE');
              this.showAlert(errorMessage);
            }*/
    },
    onDragOver(event) {
      this.isDragover = true;
    },
    onDragLeave(event) {
      this.isDragover = false;
    },
    onDrop(event) {
      this.isDragover = false;
      const files = event.dataTransfer.files;
      this.handleFiles(files);
    },
    onFileChange(event) {
      const files = this.$refs.fileInput.files;
      this.handleFiles(files);
    },
    handleFiles(files) {
      console.log('Files dropped:', files);
      // Aquí puedes procesar los archivos como desees
    },
    getPayLoad() {
      // Convertir fechas de hora local (seleccionadas por el usuario) a UTC para enviar al backend
      // toISOString() convierte el objeto Date a string ISO 8601 en formato UTC (termina en 'Z')
      // Ejemplo: Usuario en Perú (UTC-5) selecciona "2025-12-04 00:00:00" → se envía "2025-12-04T05:00:00.000Z"
      let schedule_start_at = this.campaign.startScheduleTime ? this.campaign.startScheduleTime.toISOString() : null
      let schedule_end_at = this.campaign.endScheduleTime ? this.campaign.endScheduleTime.toISOString() : null

      let run_type = 0

      if (this.campaign.sendNow) {
        run_type = 1
      }

      if (this.campaign.sendLater) {
        run_type = 2
      }

      const { params: outboundParams, static_params: staticParams } =
        buildCampaignOutboundParams(this.campaign);

      const audience =
        this.campaign.audienceSource === 'segment'
          ? [
              {
                type: 'segment',
                source_system: 'chatwoot',
                segment_id: Number(this.campaign.segmentId),
              },
            ]
          : [
              {
                type: 'file',
                source_system: 'chatwoot',
                file_id: this.campaign.fileID,
              },
            ];

      let payload = {
        name: this.campaign.title,
        flow_id: this.campaign.flowID,
        inbox_id: this.campaign.inboxID,
        audience,
        schedule_start_at: schedule_start_at,
        schedule_end_at: schedule_end_at,
        campaign_config: {
          outbound: {
            template_name: this.campaign.templateName,
            param_value_source:
              this.campaign.audienceSource === 'segment'
                ? 'contact_fields'
                : 'excel_columns',
            params: outboundParams,
            static_params: staticParams,
          },
          run_type: run_type
        }
      }

      return payload
    },
    openSendTestPopup(id) {
      this.showSendTestPopup = true;
    },
    hideSendTestPopup() {
      this.showSendTestPopup = false;
      //const currentFlow= this.flowList[this.flowList.length-1]
      //this.$router.push({ name: 'flow_manager_edit', params: { typebotId: currentFlow.typebot_id, flowId:currentFlow.id  } })
    },
  },

};
</script>
<style lang="scss" scoped>
.radio-group {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
}

.message {
  color: var(--r-500);
  font-size: var(--font-size-small);
  display: block;
  margin-top: var(--space-small);
}
</style>